import { z } from 'zod'
import type { AppSupabaseClient } from '@/infrastructure/supabase/client'
import type { SupabaseClient } from '@supabase/supabase-js'
import { reportUnexpectedError } from '@/shared/infrastructure/observability/reportUnexpectedError'
import { phoneSearchPattern, splitSearchTokens } from '@/shared/domain/searchTokens'
import { findClientIdsBySearch } from '@/infrastructure/supabase/partySearch'
import type { ClientRepository } from '../../application/ClientRepository'
import type {
  ClientContractSummary,
  ClientDossierStatus,
  ClientListFilters,
  ClientListItem,
  ClientRecord,
  ClientStats,
} from '../../domain/client'
import {
  countActiveContracts,
  deriveClientContractState,
  deriveClientDossierStatus,
  dossierContractReferences,
  deriveClientVip,
} from '../../domain/client'
import type {
  ClientNote,
  ClientNoteType,
  CreateClientNoteCommand,
  UpdateClientNoteCommand,
} from '../../domain/clientNote'
import type {
  ClientAdditionalContact,
  ClientContactRelation,
  UpsertClientAdditionalContactCommand,
} from '../../domain/clientAdditionalContact'
import { legacyClientRowSchema, mapLegacyClient } from './legacyClientMapper'

const contactRelationToDb: Record<ClientContactRelation, string> = {
  CONJOINT: 'conjoint',
  GESTIONNAIRE: 'gestionnaire',
  RESPONSABLE: 'responsable',
  AUTRE: 'autre',
}
const contactRelationFromDb: Record<string, ClientContactRelation> = {
  conjoint: 'CONJOINT',
  gestionnaire: 'GESTIONNAIRE',
  responsable: 'RESPONSABLE',
  autre: 'AUTRE',
}

const clientContactRowSchema = z.object({
  id: z.string(),
  client_id: z.string(),
  nom: z.string(),
  relation: z.string(),
  telephone: z.string().nullable(),
  telephone_mobile: z.string().nullable(),
  courriel: z.string().nullable(),
})

function mapClientContactRow(row: z.infer<typeof clientContactRowSchema>): ClientAdditionalContact {
  return {
    id: row.id,
    clientId: row.client_id,
    name: row.nom,
    relation: contactRelationFromDb[row.relation] ?? 'AUTRE',
    ...(row.telephone ? { phone: row.telephone } : {}),
    ...(row.telephone_mobile ? { mobilePhone: row.telephone_mobile } : {}),
    ...(row.courriel ? { email: row.courriel } : {}),
  }
}

const clientContactColumns = 'id,client_id,nom,relation,telephone,telephone_mobile,courriel'

const noteTypeToDb: Record<ClientNoteType, string> = {
  GENERALE: 'generale',
  SERVICE: 'service',
  FACTURATION: 'facturation',
  COMPORTEMENT: 'comportement',
}
const noteTypeFromDb: Record<string, ClientNoteType> = {
  generale: 'GENERALE',
  service: 'SERVICE',
  facturation: 'FACTURATION',
  comportement: 'COMPORTEMENT',
}

const clientNoteRowSchema = z.object({
  id: z.string(),
  client_id: z.string(),
  message: z.string(),
  note_type: z.string(),
  is_important: z.boolean(),
  is_pinned: z.boolean(),
  dismissed_from_overview: z.boolean(),
  created_at: z.string(),
  updated_at: z.string(),
  created_by_user: z.object({ email: z.string() }).nullable().optional(),
})

function mapClientNoteRow(row: z.infer<typeof clientNoteRowSchema>): ClientNote {
  return {
    id: row.id,
    clientId: row.client_id,
    message: row.message,
    noteType: noteTypeFromDb[row.note_type] ?? 'GENERALE',
    isImportant: row.is_important,
    isPinned: row.is_pinned,
    dismissedFromOverview: row.dismissed_from_overview,
    createdAt: row.created_at,
    updatedAt: row.updated_at,
    ...(row.created_by_user?.email ? { createdByEmail: row.created_by_user.email } : {}),
  }
}

const clientNoteColumns =
  'id,client_id,message,note_type,is_important,is_pinned,dismissed_from_overview,created_at,updated_at,created_by_user:users!client_notes_created_by_fkey(email)'

const clientColumns =
  'id,numero,prenom,nom,entreprise,telephone,telephone_mobile,courriel,adresse,adresse_ligne2,ville,province,code_postal,latitude,longitude,type_client,statut,statut_motif,langue,created_at,updated_at,deleted_at'

interface SupabaseResponse {
  data: unknown
  error: { message: string; code?: string } | null
  count: number | null
}

function asResponse(value: unknown): SupabaseResponse {
  return value as SupabaseResponse
}

function sanitizeSearch(value: string): string {
  return value
    .replace(/[%_,().'"\\]/g, ' ')
    .replace(/\s+/g, ' ')
    .trim()
    .slice(0, 80)
}

const clientStatsRowSchema = z.object({
  total: z.number(),
  active: z.number(),
  inactive: z.number(),
  residential: z.number(),
  commercial: z.number(),
  active_contracts: z.number(),
  vip: z.number(),
})

/** Les compteurs en une requête (`client_list_stats_v1`), mêmes règles que les filtres de la liste. */
async function loadStats(client: AppSupabaseClient, withContracts: boolean): Promise<ClientStats> {
  const rpcClient = client as unknown as SupabaseClient
  const response = asResponse(await rpcClient.rpc('client_list_stats_v1'))
  if (response.error) throw new Error(`CLIENT_COUNT_FAILED:${response.error.code ?? 'UNKNOWN'}`)
  const row: unknown = Array.isArray(response.data) ? response.data[0] : response.data
  const parsed = clientStatsRowSchema.safeParse(row)
  if (!parsed.success) throw new Error('CLIENT_COUNT_RESPONSE_INVALID')
  const stats = parsed.data
  return {
    total: stats.total,
    active: stats.active,
    inactive: stats.inactive,
    residential: stats.residential,
    commercial: stats.commercial,
    ...(withContracts ? { activeContracts: stats.active_contracts } : {}),
    vip: stats.vip,
  }
}

const contractClientIdSchema = z.object({ client_id: z.string().min(1) })

/**
 * Clients porteurs d'un Contrat dont le numéro correspond à la recherche. La
 * liste est bornée : elle sert de complément aux critères Client, pas de
 * second index.
 */
async function findClientIdsByContractNumber(client: AppSupabaseClient, search: string): Promise<string[]> {
  const response = asResponse(
    await client
      .from('contracts')
      .select('client_id')
      .is('deleted_at', null)
      .ilike('numero', `%${search}%`)
      .limit(50),
  )
  if (response.error || !Array.isArray(response.data)) return []
  const ids = new Set<string>()
  for (const candidate of response.data) {
    const parsed = contractClientIdSchema.safeParse(candidate)
    if (parsed.success) ids.add(parsed.data.client_id)
  }
  return [...ids]
}

/**
 * Refonte Clients, Phase 3 : filtre « dossier commercial » de la liste.
 * Contrairement à `loadContractStates` (bornée aux Clients déjà chargés
 * d'une page), ce filtre doit sélectionner les Clients concernés *avant*
 * la pagination — toutes les lignes `contracts` non supprimées sont donc
 * lues, regroupées par Client, puis réduites par `deriveClientDossierStatus`
 * (même règle que l'affichage). Aucune vue SQL ajoutée : requête composée
 * côté client, même patron que `findClientIdsByContractNumber` ci-dessus.
 */
async function findClientIdsByDossierStatus(
  client: AppSupabaseClient,
  dossierStatus: ClientDossierStatus,
): Promise<string[]> {
  const response = asResponse(
    await client
      .from('contracts')
      .select('client_id,statut,status_v2,saison,sent_at,sans_facturation')
      .is('deleted_at', null),
  )
  if (response.error || !Array.isArray(response.data)) return []
  const summariesByClient = groupContractSummaries(response.data)
  const ids: string[] = []
  for (const [clientId, summaries] of summariesByClient) {
    if (deriveClientDossierStatus(summaries) === dossierStatus) ids.push(clientId)
  }
  return ids
}

/** Date civile du jour à Toronto, comme l'échéance des Factures (`overdue_cents`). */
function torontoToday(now: Date = new Date()): string {
  return new Intl.DateTimeFormat('en-CA', {
    timeZone: 'America/Toronto',
    year: 'numeric',
    month: '2-digit',
    day: '2-digit',
  }).format(now)
}

const balanceRowSchema = z.object({
  client_id: z.string(),
  solde: z.coerce.number(),
  date: z.string().nullable().optional(),
})

/**
 * Solde dû des Clients de la page : Factures émises non réglées (mêmes statuts que la vue
 * « À encaisser »), portion échue selon la définition des KPI. Lecture refusée ou en erreur :
 * aucun montant, jamais un zéro.
 */
async function loadBalances(
  client: AppSupabaseClient,
  clientIds: string[],
): Promise<Map<string, { dueCents: number; overdueCents: number }> | null> {
  if (clientIds.length === 0) return null
  const response = asResponse(
    await client
      .from('invoices')
      .select('client_id,solde,date')
      .is('deleted_at', null)
      .in('statut', ['envoyee', 'partiellement_payee', 'en_retard'])
      .gt('solde', 0)
      .in('client_id', clientIds),
  )
  if (response.error || !Array.isArray(response.data)) return null
  const today = torontoToday()
  const balances = new Map<string, { dueCents: number; overdueCents: number }>()
  for (const id of clientIds) balances.set(id, { dueCents: 0, overdueCents: 0 })
  for (const candidate of response.data) {
    const parsed = balanceRowSchema.safeParse(candidate)
    if (!parsed.success) return null
    const entry = balances.get(parsed.data.client_id)
    if (!entry) continue
    const cents = Math.round(parsed.data.solde * 100)
    entry.dueCents += cents
    if (parsed.data.date && parsed.data.date.slice(0, 10) < today) entry.overdueCents += cents
  }
  return balances
}

function groupContractSummaries(data: unknown[]): Map<string, ClientContractSummary[]> {
  const summariesByClient = new Map<string, ClientContractSummary[]>()
  for (const candidate of data) {
    const parsed = contractStateRowSchema.safeParse(candidate)
    if (!parsed.success) continue
    const row = parsed.data
    const summaries = summariesByClient.get(row.client_id) ?? []
    summaries.push({
      ...(row.id ? { id: row.id } : {}),
      ...(row.numero ? { number: row.numero } : {}),
      legacyStatus: row.statut,
      ...(row.status_v2 ? { statusV2: row.status_v2 } : {}),
      ...(row.saison ? { season: row.saison } : {}),
      ...(row.sent_at ? { sentAt: row.sent_at } : {}),
      sansFacturation: row.sans_facturation === true,
    })
    summariesByClient.set(row.client_id, summaries)
  }
  return summariesByClient
}

const contractStateRowSchema = z.object({
  id: z.string().optional(),
  numero: z.string().nullable().optional(),
  client_id: z.string(),
  statut: z.string(),
  status_v2: z.string().nullable().optional(),
  saison: z.string().nullable().optional(),
  sent_at: z.string().nullable().optional(),
  sans_facturation: z.boolean().nullable().optional(),
})

/**
 * Une requête supplémentaire pour la seule page affichée : l'état métier
 * (Contrat actif, Soumission en cours…) remplace le badge `Actif`, qui ne
 * disait rien d'utile. Un échec laisse simplement les cartes sans état.
 */
async function loadContractStates(
  client: AppSupabaseClient,
  clientIds: string[],
): Promise<{ readable: boolean; states: Map<string, ClientContractSummary[]> }> {
  const states = new Map<string, ClientContractSummary[]>()
  if (clientIds.length === 0) return { readable: false, states }
  const response = asResponse(
    await client
      .from('contracts')
      .select('id,numero,client_id,statut,status_v2,saison,sent_at,sans_facturation')
      .is('deleted_at', null)
      .in('client_id', clientIds),
  )
  if (response.error || !Array.isArray(response.data)) return { readable: false, states }
  return { readable: true, states: groupContractSummaries(response.data) }
}

function mapRows(data: unknown): { clients: ClientRecord[]; anomalies: number } {
  if (!Array.isArray(data)) throw new Error('CLIENT_LIST_RESPONSE_INVALID')
  let anomalies = 0
  const clients: ClientRecord[] = []
  for (const candidate of data) {
    const parsed = legacyClientRowSchema.safeParse(candidate)
    if (!parsed.success) {
      anomalies += 1
      reportUnexpectedError(parsed.error, { feature: 'clients', operation: 'map-list-row' })
      continue
    }
    try {
      clients.push(mapLegacyClient(parsed.data))
    } catch (error) {
      anomalies += 1
      reportUnexpectedError(error, { feature: 'clients', operation: 'map-list-row' })
    }
  }
  return { clients, anomalies }
}

const createClientResultSchema = z.object({
  clientId: z.uuid(),
  number: z.string().min(1),
  displayName: z.string(),
})

const updateClientResultSchema = z.object({
  clientId: z.uuid(),
  updatedAt: z.string(),
})

const duplicateSuggestionSchema = z.object({
  clientId: z.uuid(),
  number: z.string().nullable(),
  displayName: z.string().nullable(),
  email: z.string().nullable(),
  phone: z.string().nullable(),
  matchedOn: z.enum(['PHONE', 'EMAIL', 'ADDRESS']),
})

export function createSupabaseClientRepository(client: AppSupabaseClient): ClientRepository {
  return {
    async list(filters: ClientListFilters) {
      const from = (filters.page - 1) * filters.pageSize
      const to = from + filters.pageSize - 1
      /*
       * Vues « Avec / Sans contrat actif » et VIP : jointure filtrée sur `contracts` (PostgREST),
       * résolue par la base avant la pagination. Aucune liste d'identifiants dans l'URL : sa longueur
       * ne dépend plus du nombre de Clients concernés. « Contrat actif » = `statut = 'actif'` ou
       * `status_v2 = 'ACTIVE'` (même règle que `countActiveContracts`); VIP = Contrat actif
       * `sans_facturation`. `!inner` garde les Clients qui en ont un; `!left` + `is null`, ceux qui
       * n'en ont aucun. La colonne jointe (`id` seul) est ignorée par le mapping.
       */
      const embeds = [
        ...(filters.contractActivity === 'WITH_ACTIVE' ? ['active_contracts:contracts!inner(id)'] : []),
        ...(filters.contractActivity === 'WITHOUT_ACTIVE' ? ['active_contracts:contracts!left(id)'] : []),
        ...(filters.vip ? ['vip_contracts:contracts!inner(id)'] : []),
      ]
      let query = client
        .from('clients')
        .select([clientColumns, ...embeds].join(','), { count: 'exact' })
        .order('updated_at', { ascending: false })
        .range(from, to)
      if (filters.contractActivity) {
        query = query
          .is('active_contracts.deleted_at', null)
          .or('statut.eq.actif,status_v2.eq.ACTIVE', { referencedTable: 'active_contracts' })
        if (filters.contractActivity === 'WITHOUT_ACTIVE') query = query.is('active_contracts', null)
      }
      if (filters.vip) {
        query = query
          .is('vip_contracts.deleted_at', null)
          .eq('vip_contracts.sans_facturation', true)
          .or('statut.eq.actif,status_v2.eq.ACTIVE', { referencedTable: 'vip_contracts' })
      }
      query =
        filters.status === 'ARCHIVED' ? query.not('deleted_at', 'is', null) : query.is('deleted_at', null)
      if (filters.status && filters.status !== 'ARCHIVED') {
        const statut =
          filters.status === 'ACTIVE' ? 'actif' : filters.status === 'SUSPENDED' ? 'suspendu' : 'inactif'
        query = query.eq('statut', statut)
      }
      if (filters.type)
        query = query.eq('type_client', filters.type === 'COMMERCIAL' ? 'commercial' : 'residentiel')
      const search = filters.search ? sanitizeSearch(filters.search) : ''
      // Un filtre par dossier commercial ou VIP implique la donnée qui l'affiche.
      const withContracts =
        filters.includeContractState === true ||
        Boolean(filters.dossierStatus) ||
        filters.vip === true ||
        Boolean(filters.contractActivity)
      if (filters.dossierStatus) {
        const ids = await findClientIdsByDossierStatus(client, filters.dossierStatus)
        // Aucun Client dans cet état : court-circuiter plutôt que de passer
        // un `.in()` vide (comportement PostgREST ambigu selon les versions).
        if (ids.length === 0) {
          return {
            items: [],
            page: filters.page,
            pageSize: filters.pageSize,
            total: 0,
            stats: await loadStats(client, false),
            meta: { source: 'supabase', retrievedAt: new Date().toISOString(), anomalies: 0 },
          }
        }
        query = query.in('id', ids)
      }
      // Rattrapage chantier `audit-ui-v2` (item D) : filtre Renouvellements —
      // mêmes identifiants résolus côté page (campagne ouverte), même patron
      // de court-circuit que `dossierStatus` ci-dessus.
      if (filters.clientIds) {
        if (filters.clientIds.length === 0) {
          return {
            items: [],
            page: filters.page,
            pageSize: filters.pageSize,
            total: 0,
            stats: await loadStats(client, false),
            meta: { source: 'supabase', retrievedAt: new Date().toISOString(), anomalies: 0 },
          }
        }
        query = query.in('id', filters.clientIds)
      }
      if (search) {
        // Un numéro de Contrat est une entrée de recherche courante au
        // téléphone : les Clients qui le portent rejoignent les résultats.
        // Le numéro de Contrat se cherche sur la saisie entière : le découper
        // en mots n'aurait aucun sens pour un identifiant.
        const byContract = withContracts ? await findClientIdsByContractNumber(client, search) : []
        // Recherche sans accents (« gerard » retrouve « Gérard ») : ces Clients
        // satisfont tous les mots, comme ceux retrouvés par numéro de Contrat.
        const byAccentless = await findClientIdsBySearch(client, splitSearchTokens(search))
        // Chaque mot ajoute son propre `or` : PostgREST combine les `or`
        // successifs par `and`, donc chaque mot doit correspondre à au moins
        // une colonne. C'est ce qui permet à « Patrick Goulet » et à « Goulet
        // Patrick » de retrouver le même Client.
        for (const token of splitSearchTokens(search)) {
          const clauses = [
            `numero.ilike.%${token}%`,
            `prenom.ilike.%${token}%`,
            `nom.ilike.%${token}%`,
            `entreprise.ilike.%${token}%`,
            `telephone.ilike.${phoneSearchPattern(token)}`,
            `telephone_mobile.ilike.${phoneSearchPattern(token)}`,
            `courriel.ilike.%${token}%`,
            `adresse.ilike.%${token}%`,
            `ville.ilike.%${token}%`,
            `code_postal.ilike.%${token}%`,
            // Répété dans chaque groupe : un Client retrouvé par son numéro de
            // Contrat satisfait ainsi toutes les conditions de mots.
            ...(byContract.length > 0 ? [`id.in.(${byContract.join(',')})`] : []),
            ...(byAccentless.length > 0 ? [`id.in.(${byAccentless.join(',')})`] : []),
          ]
          query = query.or(clauses.join(','))
        }
      }
      const [rawResponse, stats] = await Promise.all([query, loadStats(client, withContracts)])
      const response = asResponse(rawResponse)
      if (response.error) throw new Error(`CLIENT_LIST_FAILED:${response.error.code ?? 'UNKNOWN'}`)
      const mapped = mapRows(response.data)
      const pageIds = mapped.clients.map((record) => record.id)
      const [contractStates, balances] = await Promise.all([
        withContracts
          ? loadContractStates(client, pageIds)
          : { readable: false, states: new Map<string, ClientContractSummary[]>() },
        filters.includeBalance ? loadBalances(client, pageIds) : null,
      ])
      const items: ClientListItem[] = mapped.clients.map((record) => {
        const balance = balances?.get(record.id)
        const withBalance: ClientListItem = balance
          ? { ...record, balanceDueCents: balance.dueCents, overdueCents: balance.overdueCents }
          : record
        if (!contractStates.readable) return withBalance
        // Aucune ligne pour ce Client : il est réellement sans Contrat.
        const summaries = contractStates.states.get(record.id) ?? []
        const derived = deriveClientContractState(summaries)
        return {
          ...withBalance,
          activeContractCount: countActiveContracts(summaries),
          dossierContracts: dossierContractReferences(summaries),
          contractState: derived.state,
          ...(derived.season ? { contractSeason: derived.season } : {}),
          dossierStatus: deriveClientDossierStatus(summaries),
          vip: deriveClientVip(summaries),
        }
      })
      return {
        items,
        page: filters.page,
        pageSize: filters.pageSize,
        total: response.count ?? mapped.clients.length,
        stats,
        meta: {
          source: 'supabase',
          retrievedAt: new Date().toISOString(),
          anomalies: mapped.anomalies,
        },
      }
    },
    async getByReference(reference) {
      const uuid = /^[0-9a-f]{8}-[0-9a-f]{4}-[1-5][0-9a-f]{3}-[89ab][0-9a-f]{3}-[0-9a-f]{12}$/i.test(
        reference,
      )
      let query = client.from('clients').select(clientColumns).is('deleted_at', null)
      query = uuid ? query.eq('id', reference) : query.eq('numero', reference)
      const response = asResponse(await query.maybeSingle())
      if (response.error) throw new Error(`CLIENT_DETAIL_FAILED:${response.error.code ?? 'UNKNOWN'}`)
      if (response.data === null) return null
      const parsed = legacyClientRowSchema.safeParse(response.data)
      if (!parsed.success) throw new Error('CLIENT_DETAIL_RESPONSE_INVALID')
      return {
        client: mapLegacyClient(parsed.data),
        meta: { source: 'supabase', retrievedAt: new Date().toISOString(), anomalies: 0 },
      }
    },
    async create(command) {
      const rpcClient = client as unknown as {
        rpc: (name: string, args: Record<string, unknown>) => Promise<unknown>
      }
      const response = (await rpcClient.rpc('create_client_v2', {
        p_command: Object.fromEntries(
          Object.entries(command).filter(([, value]) => value !== undefined && value !== ''),
        ),
      })) as { data?: unknown; error?: { message?: string } | null }
      if (response.error) throw new Error(response.error.message ?? 'CLIENT_CREATE_FAILED')
      const parsed = createClientResultSchema.safeParse(response.data)
      if (!parsed.success) throw new Error('CLIENT_CREATE_RESPONSE_INVALID')
      return parsed.data
    },

    async update(command) {
      const rpcClient = client as unknown as {
        rpc: (name: string, args: Record<string, unknown>) => Promise<unknown>
      }
      const response = (await rpcClient.rpc('update_client_v1', {
        p_command: Object.fromEntries(Object.entries(command).filter(([, value]) => value !== undefined)),
      })) as { data?: unknown; error?: { message?: string } | null }
      if (response.error) throw new Error(response.error.message ?? 'CLIENT_UPDATE_FAILED')
      const parsed = updateClientResultSchema.safeParse(response.data)
      if (!parsed.success) throw new Error('CLIENT_UPDATE_RESPONSE_INVALID')
      return parsed.data
    },

    async findDuplicates(contact) {
      const rpcClient = client as unknown as {
        rpc: (name: string, args: Record<string, unknown>) => Promise<unknown>
      }
      const response = (await rpcClient.rpc('find_client_duplicates_by_contact_v1', {
        p_phone: contact.phone ?? null,
        p_email: contact.email ?? null,
        p_address: contact.address ?? null,
      })) as { data?: unknown; error?: { message?: string } | null }
      if (response.error) throw new Error(response.error.message ?? 'CLIENT_DUPLICATES_FAILED')
      const parsed = duplicateSuggestionSchema.array().safeParse(response.data)
      // Une suggestion illisible ne bloque pas la création : elle disparaît.
      return parsed.success ? parsed.data : []
    },

    async updateStatus(command) {
      const statusToDb: Record<string, string> = {
        ACTIVE: 'actif',
        SUSPENDED: 'suspendu',
        INACTIVE: 'inactif',
        ARCHIVED: 'archive',
      }
      const rpcClient = client as unknown as {
        rpc: (name: string, args: Record<string, unknown>) => Promise<unknown>
      }
      const response = (await rpcClient.rpc('update_client_status_v1', {
        p_command: {
          clientId: command.clientId,
          status: statusToDb[command.status],
          ...(command.motif ? { motif: command.motif } : {}),
        },
      })) as { data?: unknown; error?: { message?: string } | null }
      if (response.error) throw new Error(response.error.message ?? 'CLIENT_STATUS_UPDATE_FAILED')
    },

    async listNotes(clientId) {
      const response = asResponse(
        await client
          .from('client_notes')
          .select(clientNoteColumns)
          .eq('client_id', clientId)
          .is('deleted_at', null)
          .order('is_pinned', { ascending: false })
          .order('created_at', { ascending: false }),
      )
      if (response.error) throw new Error(`CLIENT_NOTES_LIST_FAILED:${response.error.code ?? 'UNKNOWN'}`)
      if (!Array.isArray(response.data)) return []
      const notes: ClientNote[] = []
      for (const candidate of response.data) {
        const parsed = clientNoteRowSchema.safeParse(candidate)
        if (parsed.success) notes.push(mapClientNoteRow(parsed.data))
        else reportUnexpectedError(parsed.error, { feature: 'clients', operation: 'map-note-row' })
      }
      return notes
    },

    async createNote(command: CreateClientNoteCommand) {
      const insertResponse = asResponse(
        await client
          .from('client_notes')
          .insert({
            client_id: command.clientId,
            message: command.message,
            note_type: noteTypeToDb[command.noteType],
            is_important: command.isImportant,
            is_pinned: command.isPinned,
          })
          .select(clientNoteColumns)
          .single(),
      )
      if (insertResponse.error)
        throw new Error(`CLIENT_NOTE_CREATE_FAILED:${insertResponse.error.code ?? 'UNKNOWN'}`)
      const parsed = clientNoteRowSchema.safeParse(insertResponse.data)
      if (!parsed.success) throw new Error('CLIENT_NOTE_CREATE_RESPONSE_INVALID')
      const note = mapClientNoteRow(parsed.data)
      await client.from('client_events').insert({
        client_id: command.clientId,
        type: 'note_creee',
        payload: { noteId: note.id, isImportant: note.isImportant, isPinned: note.isPinned },
      })
      return note
    },

    async updateNote(command: UpdateClientNoteCommand) {
      const updateResponse = asResponse(
        await client
          .from('client_notes')
          .update({
            message: command.message,
            note_type: noteTypeToDb[command.noteType],
            is_important: command.isImportant,
            is_pinned: command.isPinned,
          })
          .eq('id', command.id)
          .select(clientNoteColumns)
          .single(),
      )
      if (updateResponse.error)
        throw new Error(`CLIENT_NOTE_UPDATE_FAILED:${updateResponse.error.code ?? 'UNKNOWN'}`)
      const parsed = clientNoteRowSchema.safeParse(updateResponse.data)
      if (!parsed.success) throw new Error('CLIENT_NOTE_UPDATE_RESPONSE_INVALID')
      const note = mapClientNoteRow(parsed.data)
      await client.from('client_events').insert({
        client_id: note.clientId,
        type: 'note_modifiee',
        payload: { noteId: note.id },
      })
      return note
    },

    async softDeleteNote(noteId) {
      const response = asResponse(
        await client
          .from('client_notes')
          .update({ deleted_at: new Date().toISOString() })
          .eq('id', noteId)
          .select('client_id')
          .single(),
      )
      if (response.error) throw new Error(`CLIENT_NOTE_DELETE_FAILED:${response.error.code ?? 'UNKNOWN'}`)
      const row = response.data as { client_id?: string } | null
      if (row?.client_id) {
        await client.from('client_events').insert({
          client_id: row.client_id,
          type: 'note_supprimee',
          payload: { noteId },
        })
      }
    },

    async setNotePinned(noteId, pinned) {
      const response = asResponse(
        await client
          .from('client_notes')
          .update({ is_pinned: pinned })
          .eq('id', noteId)
          .select('client_id')
          .single(),
      )
      if (response.error) throw new Error(`CLIENT_NOTE_PIN_FAILED:${response.error.code ?? 'UNKNOWN'}`)
      const row = response.data as { client_id?: string } | null
      if (row?.client_id) {
        await client.from('client_events').insert({
          client_id: row.client_id,
          type: 'note_epinglee',
          payload: { noteId, pinned },
        })
      }
    },

    async dismissNoteFromOverview(noteId) {
      const response = asResponse(
        await client
          .from('client_notes')
          .update({ dismissed_from_overview: true })
          .eq('id', noteId)
          .select('client_id')
          .single(),
      )
      if (response.error) throw new Error(`CLIENT_NOTE_DISMISS_FAILED:${response.error.code ?? 'UNKNOWN'}`)
      const row = response.data as { client_id?: string } | null
      if (row?.client_id) {
        await client.from('client_events').insert({
          client_id: row.client_id,
          type: 'note_masquee_apercu',
          payload: { noteId },
        })
      }
    },

    async getAdditionalContact(clientId) {
      const response = asResponse(
        await client
          .from('client_additional_contacts')
          .select(clientContactColumns)
          .eq('client_id', clientId)
          .maybeSingle(),
      )
      if (response.error) throw new Error(`CLIENT_CONTACT_FETCH_FAILED:${response.error.code ?? 'UNKNOWN'}`)
      if (!response.data) return null
      const parsed = clientContactRowSchema.safeParse(response.data)
      return parsed.success ? mapClientContactRow(parsed.data) : null
    },

    async upsertAdditionalContact(command: UpsertClientAdditionalContactCommand) {
      const response = asResponse(
        await client
          .from('client_additional_contacts')
          .upsert(
            {
              client_id: command.clientId,
              nom: command.name,
              relation: contactRelationToDb[command.relation],
              telephone: command.phone ?? null,
              telephone_mobile: command.mobilePhone ?? null,
              courriel: command.email ?? null,
            },
            { onConflict: 'client_id' },
          )
          .select(clientContactColumns)
          .single(),
      )
      if (response.error) throw new Error(`CLIENT_CONTACT_SAVE_FAILED:${response.error.code ?? 'UNKNOWN'}`)
      const parsed = clientContactRowSchema.safeParse(response.data)
      if (!parsed.success) throw new Error('CLIENT_CONTACT_SAVE_RESPONSE_INVALID')
      return mapClientContactRow(parsed.data)
    },

    async deleteAdditionalContact(clientId) {
      const response = asResponse(
        await client.from('client_additional_contacts').delete().eq('client_id', clientId),
      )
      if (response.error) throw new Error(`CLIENT_CONTACT_DELETE_FAILED:${response.error.code ?? 'UNKNOWN'}`)
    },

    async delete(clientId) {
      const rpcClient = client as unknown as {
        rpc: (name: string, args: Record<string, unknown>) => Promise<unknown>
      }
      const response = (await rpcClient.rpc('delete_client_v1', {
        p_command: { clientId },
      })) as { data?: unknown; error?: { message?: string } | null }
      if (response.error) throw new Error(response.error.message ?? 'CLIENT_DELETE_FAILED')
    },
  }
}
